import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, ArrowLeft, Loader2, Briefcase, Paperclip, X, Check, MapPin } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import { API } from "@/lib/api";

const FALLBACK_ROLES = [
  "General Cleaning Operative",
  "Industrial Cleaning Specialist",
  "Cleanroom / Pharma Operative",
  "Hospital / Healthcare Cleaner",
  "Mobile Supervisor",
  "Site Manager",
  "Other / Open Application",
];
const ACCEPTED_EXTS = [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png"];
const MAX_BYTES = 15 * 1024 * 1024;

const formatBytes = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

export default function Careers() {
  const [searchParams] = useSearchParams();
  const initialRole = searchParams.get("role") || FALLBACK_ROLES[0];
  const initialJob = searchParams.get("job") || "";

  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    role: initialRole,
    location: "",
    message: "",
    job_slug: initialJob,
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/jobs?open_only=true`)
      .then((r) => setJobs(r.data || []))
      .catch(() => setJobs([]))
      .finally(() => setLoadingJobs(false));
  }, []);

  const allRoles = [...new Set([...jobs.map((j) => j.title), ...FALLBACK_ROLES])];

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = `.${f.name.split(".").pop()?.toLowerCase()}`;
    if (!ACCEPTED_EXTS.includes(ext)) {
      toast.error(`Unsupported file type. Allowed: ${ACCEPTED_EXTS.join(", ")}`);
      e.target.value = "";
      return;
    }
    if (f.size > MAX_BYTES) {
      toast.error("File too large (max 15 MB).");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const applyForJob = (job) => {
    setForm((f) => ({ ...f, role: job.title, job_slug: job.slug, location: job.location }));
    document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.full_name || !form.email || !form.phone) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("cv", file);
      await axios.post(`${API}/job-applications`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSent(true);
      setForm({ full_name: "", email: "", phone: "", role: FALLBACK_ROLES[0], location: "", message: "", job_slug: "" });
      setFile(null);
      toast.success("Application received — we'll be in touch soon.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send your application. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not send your application.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-white border border-[#E5EBF2] rounded-none px-4 py-3.5 text-[#050B14] text-base placeholder:text-[#A3A3A3] focus:border-[#0E2A57] focus:ring-1 focus:ring-[#0E2A57] focus:outline-none transition";

  const perks = [
    { k: "Family Culture", v: "Personal accountability, no faceless management" },
    { k: "Fair Pay", v: "Above-market hourly rates · weekly payslip" },
    { k: "Training", v: "On-site, paid, and progressive" },
    { k: "Branded Kit", v: "Full AGS uniform and PPE supplied" },
    { k: "Nationwide", v: "Routes across Dublin, Cork, Galway, Limerick" },
    { k: "Career Path", v: "Operative → Supervisor → Site Manager" },
  ];

  return (
    <div data-testid="careers-page" className="bg-white text-[#050B14]">
      <Navigation />

      <main>
        <section className="relative bg-[#F4F8FC] pt-40 md:pt-48 pb-16 md:pb-20 border-b border-[#E5EBF2] overflow-hidden">
          <div className="relative container-ags">
            <Link to="/" data-testid="careers-back-home" className="group inline-flex items-center gap-2 text-sm font-medium text-[#0E2A57] hover:text-[#2BB6E5] transition-colors mb-8">
              <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" />
              Back to home
            </Link>

            <div className="grid md:grid-cols-12 gap-10 items-end">
              <div className="md:col-span-8">
                <div className="flex items-center gap-3 mb-5">
                  <span className="h-px w-16 bg-[#2BB6E5]" />
                  <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
                    <span className="inline-block w-2 h-2 bg-[#2BB6E5] rounded-full animate-pulse" />
                    Careers · We're Hiring
                  </span>
                </div>
                <h1 className="font-heading font-extrabold text-5xl md:text-7xl leading-[1.0] tracking-[-0.04em] text-[#0E2A57]">
                  Join the family.
                  <br />
                  <span className="italic-display font-normal text-[#2BB6E5]">
                    Wear the navy vest.
                  </span>
                </h1>
                <p className="mt-6 text-[#475569] text-lg md:text-xl leading-relaxed max-w-2xl">
                  AGS is growing across Ireland. Browse our open roles below, or send a general application.
                </p>
              </div>
              <div className="md:col-span-4 flex md:justify-end">
                <div className="bg-white border border-[#E5EBF2] px-6 py-5 inline-flex items-center gap-4">
                  <span className="bg-[#0E2A57] text-white w-12 h-12 flex items-center justify-center">
                    <Briefcase size={22} />
                  </span>
                  <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#475569] font-medium">
                      Open positions
                    </div>
                    <div className="font-heading font-bold text-2xl text-[#0E2A57]">
                      {loadingJobs ? "…" : `${jobs.length} live`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Jobs Portal */}
        <section id="open-roles" className="bg-white py-16 md:py-24 border-b border-[#E5EBF2]">
          <div className="container-ags">
            <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
              <div>
                <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
                  Open Roles
                </span>
                <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl leading-[1.05] tracking-[-0.035em] text-[#0E2A57]">
                  Live job openings across Ireland.
                </h2>
              </div>
              <a
                href="#apply"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#0E2A57] hover:text-[#2BB6E5]"
              >
                Don't see your role? Send a general application →
              </a>
            </div>

            {loadingJobs ? (
              <div className="text-[#737373] text-sm">Loading roles…</div>
            ) : jobs.length === 0 ? (
              <div className="bg-[#F4F8FC] border border-[#E5EBF2] p-10 text-center">
                <div className="font-heading font-bold text-xl text-[#0E2A57]">
                  No live roles right now
                </div>
                <p className="mt-3 text-[#475569]">
                  We're always interested in hearing from great people. Send a general application below and we'll keep you on file.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {jobs.map((j, i) => (
                  <article
                    key={j.id}
                    data-testid={`job-card-${j.slug}`}
                    className="group bg-white border border-[#E5EBF2] hover:border-[#0E2A57]/40 hover:shadow-xl transition-all p-6 flex flex-col"
                  >
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#2BB6E5] font-medium">
                      {String(i + 1).padStart(2, "0")} · {j.employment_type}
                    </div>
                    <h3 className="mt-3 font-heading font-bold text-xl text-[#0E2A57] tracking-tight">
                      {j.title}
                    </h3>
                    <div className="mt-3 flex items-center gap-2 text-sm text-[#737373]">
                      <MapPin size={14} />
                      <span>{j.location}</span>
                    </div>
                    <p className="mt-4 text-[#475569] text-sm leading-relaxed flex-1">
                      {j.summary}
                    </p>
                    {j.pay && (
                      <div className="mt-4 text-xs text-[#0E2A57] font-semibold bg-[#BEE7F5]/40 inline-block px-2.5 py-1.5">
                        {j.pay}
                      </div>
                    )}
                    <button
                      onClick={() => applyForJob(j)}
                      data-testid={`job-apply-${j.slug}`}
                      className="mt-5 group/btn inline-flex items-center justify-between gap-3 bg-[#0E2A57] text-white hover:bg-[#081A37] px-5 py-3 text-sm font-semibold tracking-wide transition-colors w-full"
                    >
                      Apply for this role
                      <ArrowRight size={16} className="transition-transform group-hover/btn:translate-x-1" />
                    </button>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Perks */}
        <section className="bg-[#F4F8FC] py-16 md:py-24 border-y border-[#E5EBF2]">
          <div className="container-ags">
            <div className="grid md:grid-cols-12 gap-10 mb-12">
              <div className="md:col-span-5">
                <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
                  Why AGS
                </span>
                <h2 className="mt-4 font-heading font-extrabold text-3xl md:text-4xl leading-[1.05] tracking-[-0.035em] text-[#0E2A57]">
                  What you get when you wear our{" "}
                  <span className="italic-display font-normal text-[#2BB6E5]">navy vest</span>.
                </h2>
              </div>
              <p className="md:col-span-6 md:col-start-7 text-[#475569] text-base md:text-lg leading-relaxed self-end">
                We're not a faceless agency. Every operative is part of the AGS family — known by name, supported on every shift, and paid for what they're worth.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5EBF2] border border-[#E5EBF2]">
              {perks.map((p, i) => (
                <div key={p.k} className="bg-white p-6 md:p-8 hover:bg-[#F4F8FC] transition-colors">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#2BB6E5] font-medium">
                    0{i + 1}
                  </div>
                  <div className="mt-3 font-heading font-bold text-lg text-[#0E2A57]">{p.k}</div>
                  <div className="mt-2 text-[#475569] text-sm leading-relaxed">{p.v}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Application form */}
        <section id="apply" className="bg-white py-24 md:py-36">
          <div className="container-ags grid md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-5">
              <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
                Apply
              </span>
              <h2 className="mt-4 font-heading font-extrabold text-4xl md:text-5xl leading-[1.0] tracking-[-0.04em] text-[#0E2A57]">
                Send us your CV.
              </h2>
              <p className="mt-6 text-[#475569] text-lg leading-relaxed max-w-md">
                {form.job_slug
                  ? `You're applying for: ${form.role}. Attach your CV and we'll reply within a few working days.`
                  : "Tell us about yourself and where you're based. Attach your CV (PDF or DOC) and we'll reply within a few working days."}
              </p>
            </div>

            <div className="md:col-span-7">
              <form onSubmit={onSubmit} data-testid="careers-form" className="bg-[#F4F8FC] border border-[#E5EBF2] p-6 md:p-10" encType="multipart/form-data">
                {form.job_slug && (
                  <div className="mb-5 bg-white border border-[#2BB6E5]/40 px-4 py-3 flex items-center gap-3 text-sm">
                    <span className="bg-[#2BB6E5] text-[#0E2A57] w-7 h-7 flex items-center justify-center font-bold text-xs">
                      ✓
                    </span>
                    <span className="text-[#0E2A57] font-semibold">
                      Applying for: {form.role}
                    </span>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-[0.18em] text-[#737373] font-semibold mb-2">Full Name *</span>
                    <input name="full_name" value={form.full_name} onChange={onChange} required placeholder="John Murphy" data-testid="careers-input-name" className={inputClass} />
                  </label>
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-[0.18em] text-[#737373] font-semibold mb-2">Email *</span>
                    <input type="email" name="email" value={form.email} onChange={onChange} required placeholder="john@email.ie" data-testid="careers-input-email" className={inputClass} />
                  </label>
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-[0.18em] text-[#737373] font-semibold mb-2">Phone *</span>
                    <input type="tel" name="phone" value={form.phone} onChange={onChange} required placeholder="+353 87 000 0000" data-testid="careers-input-phone" className={inputClass} />
                  </label>
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-[0.18em] text-[#737373] font-semibold mb-2">Location (City / Town)</span>
                    <input name="location" value={form.location} onChange={onChange} placeholder="Dublin" data-testid="careers-input-location" className={inputClass} />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="block text-[11px] uppercase tracking-[0.18em] text-[#737373] font-semibold mb-2">Role of Interest</span>
                    <select name="role" value={form.role} onChange={onChange} data-testid="careers-input-role" className={inputClass}>
                      {allRoles.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block md:col-span-2">
                    <span className="block text-[11px] uppercase tracking-[0.18em] text-[#737373] font-semibold mb-2">Short Message</span>
                    <textarea name="message" value={form.message} onChange={onChange} rows={4} placeholder="Years of experience, availability, languages, anything else we should know…" data-testid="careers-input-message" className={`${inputClass} resize-none`} />
                  </label>

                  <div className="md:col-span-2">
                    <span className="block text-[11px] uppercase tracking-[0.18em] text-[#737373] font-semibold mb-2">Attach CV</span>
                    <input id="cv-input" type="file" accept={ACCEPTED_EXTS.join(",")} onChange={onFile} data-testid="careers-input-cv" className="hidden" />
                    {!file ? (
                      <label htmlFor="cv-input" className="w-full bg-white border border-dashed border-[#0E2A57]/40 hover:border-[#0E2A57] px-5 py-5 flex items-center justify-between gap-4 cursor-pointer transition-colors text-left group">
                        <div className="flex items-center gap-3">
                          <span className="bg-[#0E2A57] text-white w-10 h-10 flex items-center justify-center">
                            <Paperclip size={16} />
                          </span>
                          <div>
                            <div className="font-semibold text-sm text-[#0E2A57]">Upload your CV</div>
                            <div className="text-xs text-[#737373] mt-0.5">PDF · DOC · DOCX · JPG · PNG · max 15 MB</div>
                          </div>
                        </div>
                        <span className="text-xs uppercase tracking-[0.18em] text-[#0E2A57] font-semibold opacity-70 group-hover:opacity-100">Browse →</span>
                      </label>
                    ) : (
                      <div className="w-full bg-white border border-[#0E2A57]/40 px-5 py-4 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="bg-[#2BB6E5] text-[#0E2A57] w-10 h-10 flex items-center justify-center font-bold">
                            {file.name.split(".").pop()?.toUpperCase().slice(0, 4)}
                          </span>
                          <div className="min-w-0">
                            <div className="font-semibold text-sm text-[#0E2A57] truncate">{file.name}</div>
                            <div className="text-xs text-[#737373] mt-0.5">{formatBytes(file.size)}</div>
                          </div>
                        </div>
                        <button type="button" onClick={() => setFile(null)} className="text-[#737373] hover:text-[#0E2A57] p-2" aria-label="Remove CV">
                          <X size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
                  <p className="text-xs text-[#737373] max-w-sm">
                    By applying, you agree we may contact you about your application and process your CV for hiring purposes.
                  </p>
                  <button type="submit" disabled={loading} data-testid="careers-submit" className="group inline-flex items-center gap-3 bg-[#0E2A57] text-white hover:bg-[#081A37] disabled:opacity-60 disabled:cursor-not-allowed px-10 py-5 font-semibold tracking-wide transition-colors">
                    {loading ? (<><Loader2 size={18} className="animate-spin" /> Sending…</>) : (<>Send Application <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" /></>)}
                  </button>
                </div>

                {sent && (
                  <div data-testid="careers-success" className="mt-6 border border-[#0E2A57]/30 bg-[#BEE7F5]/30 text-[#081A37] p-4 text-sm">
                    Thank you — your application has been received. We'll be in touch shortly.
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
